import Swal from "sweetalert2";

/**
 * Session expiry handling.
 *
 * Thirteen call sites across seven screens reacted to a 401 by silently
 * clearing localStorage and assigning window.location.href — the user was
 * signed out and thrown to the sign-in form mid-task with no explanation, and
 * anything unsaved went with it. Worse, a screen that fires several requests
 * could hit this more than once and stack the redirects.
 *
 * This centralises it: say what happened, wait for the user to acknowledge,
 * then redirect once.
 */

export const SESSION_EXPIRED_TITLE = "Your session has expired";

export const SESSION_EXPIRED_MESSAGE =
  "You have been signed out because your session expired or was signed out elsewhere. Please sign in again to continue.";

/** True when an axios error is an authentication failure. */
export const isUnauthorised = (error) => error?.response?.status === 401;

// A screen can have several requests in flight; the first 401 wins and the
// rest are ignored, so the user sees one dialog and one redirect.
let expiryInProgress = false;

/**
 * Sign the user out and send them to the sign-in form, after telling them why.
 *
 * Returns a promise that resolves once the redirect has been triggered, so a
 * caller can `await` it, but callers are not required to.
 */
export const handleSessionExpiry = async ({
  signInPath = "/Signup",
} = {}) => {
  if (expiryInProgress) return;
  expiryInProgress = true;

  // Clear the credential first: whatever the user does with the dialog, the
  // dead token must not survive it.
  localStorage.removeItem("user");

  try {
    await Swal.fire({
      icon: "warning",
      title: SESSION_EXPIRED_TITLE,
      text: SESSION_EXPIRED_MESSAGE,
      confirmButtonText: "Sign in",
      allowOutsideClick: false,
    });
  } finally {
    window.location.href = signInPath;
  }
};

/**
 * Convenience for the common `catch` shape. Returns true when the error was a
 * 401 and the expiry flow has been started, so the caller can stop handling it:
 *
 *   catch (error) {
 *     if (handleIfSessionExpired(error)) return;
 *     ...normal error handling
 *   }
 */
export const handleIfSessionExpired = (error) => {
  if (!isUnauthorised(error)) return false;
  handleSessionExpiry();
  return true;
};

// Test seam — the module-level guard would otherwise leak between test cases.
export const __resetSessionExpiryGuard = () => {
  expiryInProgress = false;
};
